import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { BaseButton, useT } from '@repo/common';

import NotionRenderer from '../../components/Article/NotionRenderer';
import useDocumentMeta from '../../components/Article/useDocumentMeta';
import MetaTag, { buildMetaTags } from '../../components/Article/MetaTag';
import { formatDate } from '../../components/Article/ArticleCard';
import { getArticleBySlug } from '../../api/article/article.api';

/**
 * 본문 컨테이너.
 * 데스크탑에서 1024px로 제한하고 가운데 정렬. 좌우 여백은 화면이 좁아질수록 줄인다.
 */
const CONTAINER_CLASS = 'mx-auto w-full max-w-[1024px]';

const preventCopy = (event) => event.preventDefault();

const DetailSkeleton = () => (
  <div className='flex animate-pulse flex-col gap-4'>
    <div className='h-9 w-4/5 rounded bg-grayscale-gray01' />
    <div className='h-5 w-52 rounded bg-grayscale-gray01' />
    <div className='mt-4 h-[320px] w-full rounded-[16px] bg-grayscale-gray01' />
    <div className='mt-4 h-4 w-full rounded bg-grayscale-gray01' />
    <div className='h-4 w-11/12 rounded bg-grayscale-gray01' />
    <div className='h-4 w-9/12 rounded bg-grayscale-gray01' />
  </div>
);

const ArticleDetail = () => {
  const t = useT();
  const { slug } = useParams();
  const navigate = useNavigate();

  const [article, setArticle] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isNotFound, setIsNotFound] = useState(false);

  useEffect(() => {
    let ignore = false;
    setIsLoading(true);
    setIsNotFound(false);
    window.scrollTo(0, 0);

    getArticleBySlug(slug)
      .then((data) => {
        if (ignore) return;
        if (!data) {
          setIsNotFound(true);
          return;
        }
        setArticle(data);
      })
      .catch(() => {
        if (!ignore) setIsNotFound(true);
      })
      .finally(() => {
        if (!ignore) setIsLoading(false);
      });

    return () => {
      ignore = true;
    };
  }, [slug]);

  useDocumentMeta({
    title: article
      ? `${article.seoTitle || article.title} | Koready`
      : undefined,
    description: article?.seoDescription,
    image: article?.coverImageUrl,
    url: typeof window !== 'undefined' ? window.location.href : undefined,
  });

  if (isNotFound) {
    return (
      <div className='flex min-h-screen w-full flex-col items-center gap-4 bg-grayscale-gray00 px-4 py-24 text-center'>
        <p className='typo-title1 text-text-primary'>
          {t('Article not found', '아티클을 찾을 수 없어요')}
        </p>
        <p className='typo-b14 text-text-placeholder'>
          {t(
            'This article may have been moved or unpublished.',
            '이동되었거나 비공개로 바뀐 글일 수 있어요.',
          )}
        </p>
        <BaseButton
          type='primary'
          size='m'
          hideIcon
          onClick={() => navigate('/article')}
        >
          {t('Back to Articles', '아티클 목록으로')}
        </BaseButton>
      </div>
    );
  }

  const metaTags = article
    ? buildMetaTags(article, formatDate(article.publishedAt))
    : [];

  return (
    <div className='w-full min-h-screen bg-grayscale-gray00 pb-20 desktopL:bg-grayscale-gray01 tabletH:p-6 tabletH:pb-20 desktopL:p-20 desktopL:pt-6'>
      <div className={CONTAINER_CLASS}>
        {isLoading || !article ? (
          <div className='p-4 tabletH:p-6'>
            <DetailSkeleton />
          </div>
        ) : (
          <article
            onCopy={preventCopy}
            onCut={preventCopy}
            onContextMenu={preventCopy}
            onDragStart={preventCopy}
            className='w-full select-none overflow-hidden bg-grayscale-gray00 [-webkit-touch-callout:none]'
          >
            <header className='flex flex-col gap-4 p-4 tabletH:p-6'>
              <h1 className='typo-title1 tabletH:typo-h6 text-text-primary'>
                {article.title}
              </h1>

              {metaTags.length > 0 && (
                <div className='flex flex-wrap items-center gap-1.5'>
                  {metaTags.map((tag, index) => (
                    <MetaTag key={`${tag}-${index}`}>{tag}</MetaTag>
                  ))}
                </div>
              )}
            </header>

            {article.coverImageUrl && (
              <img
                src={article.coverImageUrl}
                alt={article.title}
                className='w-full object-cover'
              />
            )}

            <NotionRenderer
              blocks={article.blocks}
              className='p-4 pb-[60px] tabletH:p-6 tabletH:pb-[60px]'
            />
          </article>
        )}
      </div>
    </div>
  );
};

export default ArticleDetail;
